import { categories } from "@/mocks/categories";
import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const linkClass = scrolled
    ? "text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
    : "text-sm font-medium text-background-50/90 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap";

  const logoPrimaryClass = scrolled
    ? "text-primary-500"
    : "text-primary-300";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <span className={`text-lg font-bold tracking-tight transition-colors duration-300 ${scrolled ? "text-foreground-950" : "text-background-50"}`}>
              AllChinaBuy <span className={logoPrimaryClass}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className={linkClass}>
              Home
            </Link>

            <Link to="/how-to-use-allchinabuy-spreadsheet" className={linkClass}>
              Buying Guide
            </Link>

            <Link to="/allchinabuy-spreadsheet-trusted-sellers" className={linkClass}>
              Trusted Sellers
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap ${scrolled ? "text-sm font-medium text-foreground-700 hover:text-primary-500" : "text-sm font-medium text-background-50/90 hover:text-background-50"}`}
              >
                Categories
                <i
                  className={`ri-arrow-down-s-line transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 rounded-lg border border-background-200/70 shadow-lg py-2 w-56 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy It Now
              <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
            </a>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-500 text-background-50 text-xs font-semibold rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Buy Now
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`cursor-pointer transition-colors ${scrolled ? "text-foreground-800" : "text-background-50"}`}
              aria-label="Toggle menu"
            >
              <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-background-200/30 mt-2 pt-3">
            <Link
              to="/"
              className="block py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-home-line w-4 h-4 inline-flex items-center justify-center mr-2"></i>
              Home
            </Link>
            <Link
              to="/how-to-use-allchinabuy-spreadsheet"
              className="block py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-book-open-line w-4 h-4 inline-flex items-center justify-center mr-2"></i>
              Buying Guide
            </Link>
            <Link
              to="/allchinabuy-spreadsheet-trusted-sellers"
              className="block py-2.5 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="ri-shield-check-line w-4 h-4 inline-flex items-center justify-center mr-2"></i>
              Trusted Sellers
            </Link>
            <div className="border-t border-background-200/50 mt-2 pt-3">
              <p className="py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                Categories
              </p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
            <div className="border-t border-background-200/50 mt-3 pt-3">
              <p className="py-2 text-xs font-semibold text-foreground-500 uppercase tracking-wide">
                SEO Guides
              </p>
              <Link
                to="/allchinabuy-spreadsheet-sneakers"
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Best Sneakers Spreadsheet
              </Link>
              <Link
                to="/allchinabuy-spreadsheet-shipping"
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Shipping Guide
              </Link>
              <Link
                to="/allchinabuy-spreadsheet-qc-guide"
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                QC Guide
              </Link>
              <Link
                to="/allchinabuy-spreadsheet-safe-buying"
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Safe Buying Guide
              </Link>
              <Link
                to="/allchinabuy-spreadsheet-comparison"
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Platform Comparison
              </Link>
              <Link
                to="/allchinabuy-spreadsheet-price-guide"
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Price Guide
              </Link>
              <Link
                to="/allchinabuy-spreadsheet-2026-updates"
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                2026 Updates
              </Link>
              <Link
                to="/allchinabuy-spreadsheet-beginners"
                className="block py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                Beginner Guide
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}